import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Pressable } from 'react-native';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';

import styles from './Slider.module.css';

import SliderDots from './components/SliderDots/SliderDots'
import { SliderProps } from './Slider.types';

function Slider<Item>({
  items,
  renderItem,
  onItemPress,
  autoplay = true,
  loop = true,
  viewportOverflow = 'hidden',
  slideOverflow = 'hidden',
  viewportClipOffsetTop = 0,
  autoPlayDurationPerItem = 3000,
}: SliderProps<Item>) {
  const shouldEnableSlider = items.length > 1;

  const autoplayPlugin = useMemo(
    () =>
      Autoplay({ delay: autoPlayDurationPerItem, stopOnInteraction: false }),
    [autoPlayDurationPerItem],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: shouldEnableSlider && loop,
      watchDrag: shouldEnableSlider,
    },

    shouldEnableSlider && autoplay ? [autoplayPlugin] : [],
  );

  useEffect(() => {
    if (!emblaApi || !shouldEnableSlider || !autoplay) {
      return;
    }

    const autoplayApi = emblaApi.plugins().autoplay;

    if (!autoplayApi) {
      return;
    }

    if (!autoplay) {
      autoplayApi.stop();
      return;
    }

    autoplayApi.play();
  }, [emblaApi, autoplay, shouldEnableSlider]);

  const [activeIndex, setActiveIndex] = useState(0);

  const dotWidths = useRef(
    items.map((_, index) => new Animated.Value(index === 0 ? 16 : 6)),
  ).current;

  const onSelect = useCallback(() => {
    if (!emblaApi) {
      return;
    }

    setActiveIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi || !shouldEnableSlider) {
      return;
    }

    emblaApi.on('select', onSelect);

    return () => {
      emblaApi.off('select', onSelect);
    };
  }, [emblaApi, onSelect, shouldEnableSlider]);

  useEffect(() => {
    dotWidths.forEach((anim, index) => {
      Animated.timing(anim, {
        toValue: index === activeIndex ? 16 : 6,
        duration: 300,
        useNativeDriver: false,
      }).start();
    });
  }, [activeIndex, dotWidths]);

  return (
    <>
      <div
        className={styles.emblaViewport}
        ref={emblaRef}
        style={{
          overflow: viewportOverflow,
          paddingTop: viewportClipOffsetTop,
          marginTop: viewportClipOffsetTop * -1,
        }}
      >
        <div className={styles.emblaContainer}>
          {items.map((item, index) => (
            <div
              className={styles.emblaSlide}
              key={index}
              style={{ overflow: slideOverflow }}
            >
              <Pressable
                onPress={() => onItemPress?.(item, index)}
                disabled={!onItemPress}
              >
                {renderItem(item, index)}
              </Pressable>
            </div>
          ))}
        </div>
      </div>

      <SliderDots
        itemsCount={items.length}
        activeIndex={activeIndex}
        dotWidths={dotWidths}
      />
    </>
  );
}

export default Slider;